'use client';

import React, { useContext, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useClockSettingsStore, useClockStore } from '@/store/timeStore';
import { TimezoneData } from '@/api/timezone/timezoneData';
import { ClockSizeContext, DialTextureContext } from './ClockRing';
import { PillGlass } from '../LiquidGlass/PillGlass';

// --- Pill sizing (responsive to the rendered dial size) -------------------
//
// iOS reference (TimeYWimeyClockSurface.swift 856-1023, 1361-1386) uses
// a fixed 16px font and roughly 130-140px wide pills on a ~360-400px dial
// — about 35% of dial width. Web has to handle a wider range: a 1280x800
// desktop yields a 512px dial (pill ≈ 140px is fine), but a 393x852 iPhone
// yields a 181px dial where a fixed 140px pill is 77% of the dial and the
// chain explodes.
//
// We scale all geometry off the actual measured dial width via the
// `ClockSizeContext` from `ClockRing`. Targets:
//   - Pill height ≈ 8% of dial size (32px on a 400px dial; 14.5px on 181).
//     Min 24px, max 36px so glyphs stay legible.
//   - Pill width ≈ 28% of dial size for primary, 26% for secondary.
//     Min 78/72px (fits "12:51 PM" at the 11px font min), max 144/132px.
//   - Time font ≈ 3.5% of dial size. Min 11px, max 14px.
//   - Flag glyph ≈ 6.5% of dial size. Min 16px, max 22px.
//   - Padding 10px h / 8px v from iOS, scaled proportionally.
//
// Tab-overlap formula (iOS line 953-957): ceil((h₁+h₂)/2)+2px. We compute
// it from the scaled heights at render time.
function clamp(min: number, value: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

interface PillMetrics {
  primaryHeight: number;
  tabHeight: number;
  primaryWidth: number;
  secondaryWidth: number;
  tabOverlap: number;
  secondaryOverlap: number;
  timeFontPx: number;
  flagFontPx: number;
  contentGap: number;
  horizontalPad: number;
}

function metricsFor(clockPx: number, hour12: boolean): PillMetrics {
  // All targets calibrated against iOS at a 400px dial.
  // iOS sizes primary and tab capsule heights from the same content metrics
  // (`flagHeight + 2*verticalPadding`) so they end up equal-or-nearly so. We
  // mirror that here — equal heights eliminate the visible stacked-pill
  // appearance at the connection point. With equal heights the iOS overlap
  // formula collapses to `tabOverlap = primaryHeight + 2`.
  const primaryHeight = clamp(24, Math.round(clockPx * 0.08), 36);
  const tabHeight = primaryHeight;
  const tabOverlap = Math.ceil((primaryHeight + tabHeight) / 2) + 2;
  const secondaryOverlap = Math.ceil(tabHeight) + 2;
  const timeFontPx = clamp(11, Math.round(clockPx * 0.035), 14);
  const flagFontPx = clamp(16, Math.round(clockPx * 0.065), 22);
  const contentGap = clamp(4, Math.round(clockPx * 0.014), 6);
  const horizontalPad = clamp(7, Math.round(clockPx * 0.022), 10);

  // Pill width estimate from content: time text width + gap + flag + 2*pad.
  // Time text width ≈ 0.62 * fontPx per tabular-nums char (calibrated for
  // SF Pro Rounded Bold). 12h has 8 chars max ("12:51 PM"), 24h has 5
  // ("23:59"). Flag emoji renders ~1.3em wide.
  const timeChars = hour12 ? 8 : 5;
  const timeWidthEstimate = Math.round(timeFontPx * 0.62 * timeChars);
  const flagGlyphWidth = Math.round(flagFontPx * 1.3);
  const primaryWidth = clamp(
    hour12 ? 78 : 64,
    timeWidthEstimate + contentGap + flagGlyphWidth + horizontalPad * 2 + 6,
    hour12 ? 144 : 116,
  );
  const secondaryWidth = clamp(
    hour12 ? 72 : 60,
    timeWidthEstimate + contentGap + flagGlyphWidth + horizontalPad * 2,
    hour12 ? 132 : 108,
  );

  return {
    primaryHeight,
    tabHeight,
    primaryWidth,
    secondaryWidth,
    tabOverlap,
    secondaryOverlap,
    timeFontPx,
    flagFontPx,
    contentGap,
    horizontalPad,
  };
}

// Spring config maps iOS `spring(response: 0.28, dampingFraction: 0.86)`
// → Framer Motion `{ stiffness: 400, damping: 28 }`.
const PLACEMENT_SPRING = { type: 'spring' as const, stiffness: 400, damping: 28 };

type Placement = 'left' | 'right';

interface ClusterClassNames {
  primary?: string;
  tab?: string;
  flag?: string;
}

interface ClusterMarkerProps {
  /** Cities in this 15-min angle bucket, primary first (home promoted if present). */
  cities: TimezoneData[];
  baseTime: Date;
  homeCity: string | null;
  /** Dial radius as a percentage of the clock face (0-50). */
  radius: number;
  classNames?: ClusterClassNames;
  homeAccentClassName?: string;
}

// --- Time / angle helpers -------------------------------------------------

function getTimeForCity(timezone: string, baseTime: Date): Date {
  return new Date(baseTime.toLocaleString('en-US', { timeZone: timezone }));
}

/** ((h + m/60 + (s + ms/1000)/3600) / 24) * 360 — full sub-second precision so the
 *  pill never visibly drifts behind the actual local time during a drag.
 *  Mirrors iOS markerAngle (Swift line 1815-1825). */
function cityAngle(city: TimezoneData, baseTime: Date): number {
  const t = getTimeForCity(city.timezone, baseTime);
  const h = t.getHours();
  const m = t.getMinutes();
  const s = t.getSeconds();
  const ms = t.getMilliseconds();
  const totalHours = h + m / 60 + (s + ms / 1000) / 3600;
  return ((totalHours / 24) * 360) % 360;
}

function naturalCityAngle(city: TimezoneData): number {
  const t = getTimeForCity(city.timezone, new Date());
  const h = t.getHours();
  const m = t.getMinutes();
  return ((h + m / 60) / 24) * 360;
}

/** Drop secondaries whose emoji matches the primary's, then dedupe among
 *  themselves, then cap at MAX_TAB_FLAGS so the single tab capsule stays
 *  bounded. Mirrors iOS `appClusterTabFlagCities` (Swift line 900-908).
 *
 *  iOS uses ONE tab capsule containing only flag glyphs (no time text);
 *  3+ unique-flag secondaries fall back to the hover popover. */
const MAX_TAB_FLAGS = 3;
function tabFlagsFor(primary: TimezoneData, secondaries: TimezoneData[]): TimezoneData[] {
  const seen = new Set<string>();
  if (primary.emoji) seen.add(primary.emoji);
  const out: TimezoneData[] = [];
  for (const c of secondaries) {
    if (!c.emoji) continue;
    if (seen.has(c.emoji)) continue;
    seen.add(c.emoji);
    out.push(c);
    if (out.length >= MAX_TAB_FLAGS) break;
  }
  return out;
}

// --- Defaults consumed by `surfaces` via the `classNames` prop ------------
// Liquid-glass capsule: NO opaque `material-thin` fill (60% white would wash
// out the refraction). The capsule is a transparent `overflow-hidden` round
// rect; a `PillGlass` refraction layer + a reduced time-of-day tint + the
// content stack inside it. The specular bevel lives in `GLASS_BEVEL_SHADOW`.
const DEFAULT_PRIMARY_CN = 'relative rounded-full overflow-hidden';
const DEFAULT_TAB_CN = 'relative rounded-full overflow-hidden';
const DEFAULT_FLAG_CN = 'relative z-10';
// Specular rim/bevel + soft drop shadow that read the capsule as lit glass.
const GLASS_BEVEL_SHADOW = [
  'inset 0 1px 1px rgba(255,255,255,0.7)',
  'inset 0 -1px 1px rgba(255,255,255,0.25)',
  'inset 0 0 0 1px rgba(255,255,255,0.22)',
  '0 4px 10px rgba(0,0,0,0.12)',
].join(', ');
// iOS Liquid Glass is neutral, not colour-tinted. Instead of the old time-of-day
// colour wash (which muddied pills over the dark night gradient and read as
// "tinted"), lay a faint NEUTRAL frost of the dial-face colour. It lifts text
// legibility over dark backdrop regions and adapts light/dark, while the
// refracted conic gradient behind the glass still conveys time-of-day on its own.
// Mode-aware frost (alpha baked into the CSS var): a light scrim in light mode
// (lifts the dark night-gradient region for dark text), a darker scrim in dark
// mode (so pills read as clean dark glass with legible light text instead of a
// muddy half-transparent veil over the bright gradient). See `--pill-frost`.
const GLASS_FROST = 'var(--pill-frost)';
// iOS home indicator: circle ring around the FLAG glyph only (not the
// whole pill). Swift line 1391-1404: `Circle().strokeBorder(accent, lineWidth: 2.25)`
// with 3px padding inside the ring.
const HOME_FLAG_RING_COLOR = '#2563EB';
const HOME_FLAG_RING_WIDTH = 2.25;
const HOME_FLAG_RING_PADDING = 3;

function formatCityTime(city: TimezoneData, baseTime: Date, timeFormat: Intl.DateTimeFormatOptions): string {
  const t = getTimeForCity(city.timezone, baseTime);
  return t.toLocaleTimeString('en-US', timeFormat);
}

export function ClusterMarker({
  cities,
  baseTime,
  homeCity,
  radius,
  classNames = {},
  homeAccentClassName,
}: ClusterMarkerProps) {
  const isDragging = useClockSettingsStore((s) => s.globalIsDragging);
  const timeFormat = useClockSettingsStore((s) => s.timeFormat);
  const isAdjusted = useClockStore((s) => s.isAdjusted);
  // Pixel size of the rendered clock face — drives all pill dimensions
  // so the marker layout stays proportional from a 181px iPhone dial up
  // to a 512px desktop dial.
  const clockPx = useContext(ClockSizeContext);
  // Transparent texture of the dial's labels + crosshairs (see ClockRing).
  const dialTexture = useContext(DialTextureContext);
  const metrics = useMemo(
    () => metricsFor(clockPx, timeFormat.hour12),
    [clockPx, timeFormat.hour12],
  );
  const {
    primaryHeight,
    tabHeight,
    primaryWidth,
    tabOverlap,
    timeFontPx,
    flagFontPx,
    contentGap,
    horizontalPad,
  } = metrics;

  if (!cities.length) return null;

  const primary = cities[0];
  const secondaries = cities.slice(1);
  const tabFlags = tabFlagsFor(primary, secondaries);
  const showsTab = tabFlags.length > 0;
  const primaryIsHome = primary.locationKey === homeCity;

  // Anchor the cluster at the primary's actual angle (mirrors iOS:
  // secondaries adopt the primary's exact dial position because they
  // share the same displayed time string). We snap to the bucket only
  // for clustering decisions, not for placement.
  const angle = cityAngle(primary, baseTime);
  const radians = (angle + 90) * (Math.PI / 180);
  const dotX = 50 + radius * Math.cos(radians);
  const dotY = 50 + radius * Math.sin(radians);

  // Trail-arc geometry — only meaningful when home is the primary AND the
  // base time has been adjusted away from "now" (otherwise start == end and
  // the SVG arc collapses to a zero-length path with spec-undefined render
  // behavior).
  const naturalAngle = naturalCityAngle(primary);
  const naturalRadians = (naturalAngle + 90) * (Math.PI / 180);
  const naturalX = 50 + radius * Math.cos(naturalRadians);
  const naturalY = 50 + radius * Math.sin(naturalRadians);
  const angleDiff = ((angle - naturalAngle + 540) % 360) - 180;
  const shouldTakeShortPath = Math.abs(angleDiff) <= 180;
  const sweepFlag = angleDiff > 0;
  // Arc only shows when the user has actually dragged the dial. Without
  // the `isAdjusted` gate, the seconds/ms precision in `cityAngle` vs the
  // hour+minute precision in `naturalCityAngle` would produce a tiny
  // non-zero angleDiff every render in live mode — drawing a hairline arc
  // even when nothing has been adjusted.
  const renderTrailArc =
    primaryIsHome && isAdjusted && Math.abs(angleDiff) > 0.01;

  // Placement (iOS line 1076-1080, 1376-1387): bucketed-angle determines
  // which side of the dial the primary city sits on, which then drives:
  //   1) The flag/time order inside the primary pill (flag is always on
  //      the OUTWARD side, time on the INWARD side).
  //   2) Which side of the primary the cluster tab extends from
  //      (always toward the dial center, on the time-side of the primary).
  //
  //   placement.left  (angle 0-180°, LEFT half of dial):
  //     primary = [FLAG][TIME]; tab extends RIGHT (inward toward center).
  //   placement.right (angle 180-360°, RIGHT half of dial):
  //     primary = [TIME][FLAG]; tab extends LEFT (inward toward center).
  //
  // Bucket-snap to 5° (iOS) — we use our existing 3.75° bucket — so float
  // jitter around the 180° boundary doesn't oscillate placement frame-to-
  // frame during a drag.
  const bucketedAngle = clusterBucketKey(angle) * 3.75;
  const placement: Placement = bucketedAngle < 180 ? 'left' : 'right';
  // Sign of the tab's x offset from primary center. .left → tab on right
  // of primary (positive). .right → tab on left of primary (negative).
  const tabSideSign = placement === 'left' ? 1 : -1;

  // Convert pixel offsets along the screen-x axis to dial percentage
  // coords so absolute-positioned children can use `left: ${x}%`.
  const pxToDialPercent = (px: number): number =>
    clockPx > 0 ? (px / clockPx) * 100 : 0;

  // Single-tab geometry (iOS line 855-940). The tab is one capsule that
  // contains all secondary flag glyphs side-by-side with no time text.
  // Width = hiddenInset (overlap region) + innerGap + flagsWidth + gaps +
  // outerPadding.
  const flagGlyphWidthPx = Math.round(flagFontPx * 1.3);
  const flagsWidthPx = flagGlyphWidthPx * tabFlags.length;
  const flagsGapsPx = Math.max(0, tabFlags.length - 1) * contentGap;
  const tabInnerGap = 2;
  const tabOuterPad = 8;
  const tabWidth = tabOverlap + tabInnerGap + flagsWidthPx + flagsGapsPx + tabOuterPad;

  // iOS flag-anchor positioning (TimeYWimeyClockSurface.swift:1437-1461):
  // the FLAG's screen center sits at the orbit point, not the pill center.
  // The pill body then extends INWARD (toward dial center) from the flag.
  // Without this, a 46% orbit would place the pill's outer edge at ~60%
  // of dial diameter — well outside the dial face.
  //
  //   placement.left  pill = [FLAG][TIME] → flag at left of pill,
  //                          pill center sits to the RIGHT of flag center.
  //   placement.right pill = [TIME][FLAG] → flag at right of pill,
  //                          pill center sits to the LEFT of flag center.
  //
  // Offset from flag center to pill center, in pixels:
  //   abs(offset) = primaryW/2 - horizontalPad - flagGlyphWidth/2
  // Signed by tabSideSign (+1 for placement.left, -1 for placement.right)
  // so the pill body always extends INWARD toward the dial center.
  const flagCenterToPillCenterPx =
    primaryWidth / 2 - horizontalPad - flagGlyphWidthPx / 2;
  const primaryCenterDotX =
    dotX + tabSideSign * pxToDialPercent(flagCenterToPillCenterPx);
  const primaryCenterDotY = dotY;
  // Tab center sits beyond the primary on the time-side — same chord
  // direction (toward center). Tab is offset from PRIMARY center, then
  // converted to dial-percent and added to the primary's dot position.
  const tabCenterPxOffset =
    tabSideSign * (primaryWidth / 2 + tabWidth / 2 - tabOverlap);
  const tabCenterDotX =
    primaryCenterDotX + pxToDialPercent(tabCenterPxOffset);
  const tabCenterDotY = primaryCenterDotY;

  // --- Liquid-glass refraction backdrop -----------------------------------
  // Each capsule refracts a faithful copy of the dial behind it: the conic
  // gradient INSIDE the 42%-radius disc, and the solid dial-face colour OUTSIDE
  // it (the white border ring the pills orbit on). Reproducing both — rather
  // than gradient-everywhere — keeps a pill's outer portion matching the white
  // ring instead of bleeding the (often dark) gradient across the whole pill.
  //
  // `ClockBackground` paints the gradient on a disc inset 8% of the dial (box
  // 0.84*D, `at 50% 50%` → dial centre) over a full dial-face circle. We rebuild
  // that as two background layers offset to this pill's slice, derived from the
  // known center-percent + size — no DOM measurement, no per-frame snapshot.
  const D = clockPx;
  const gradInsetPx = 0.08 * D; // conic-gradient box top-left in dial px
  const gradBoxPx = 0.84 * D; // conic-gradient box size
  const gradRadiusPx = 0.42 * D; // visible gradient disc radius (inset 8%)
  const DIAL_FACE = 'var(--clock-dial)';
  const DIAL_GRADIENT = 'var(--clock-dial-gradient)';
  const backdropFor = (centerPercentX: number, centerPercentY: number, w: number, h: number) => {
    const pillLeftPx = (centerPercentX / 100) * D - w / 2;
    const pillTopPx = (centerPercentY / 100) * D - h / 2;
    // Dial centre expressed in this pill's local (inset-0) coordinates.
    const cX = 0.5 * D - pillLeftPx;
    const cY = 0.5 * D - pillTopPx;
    // Top layer (optional): the captured labels/crosshairs texture, a full-dial
    // (D×D) image offset to this pill's slice so they refract through the glass.
    const texLayer = dialTexture
      ? { img: `url("${dialTexture}"), `, size: `${D}px ${D}px, `, pos: `${(-pillLeftPx).toFixed(2)}px ${(-pillTopPx).toFixed(2)}px, ` }
      : { img: '', size: '', pos: '' };
    return {
      // Layer order (top→bottom): labels/crosshairs texture · a hard-edged
      // radial that's transparent inside the gradient disc and dial-face colour
      // outside it · the conic gradient itself.
      image: `${texLayer.img}radial-gradient(circle ${gradRadiusPx}px at ${cX.toFixed(2)}px ${cY.toFixed(2)}px, transparent 0 ${(gradRadiusPx - 0.5).toFixed(2)}px, ${DIAL_FACE} ${gradRadiusPx.toFixed(2)}px), ${DIAL_GRADIENT}`,
      size: `${texLayer.size}${D}px ${D}px, ${gradBoxPx}px ${gradBoxPx}px`,
      position: `${texLayer.pos}0px 0px, ${(gradInsetPx - pillLeftPx).toFixed(2)}px ${(gradInsetPx - pillTopPx).toFixed(2)}px`,
    };
  };
  // "Subtle" preset scaled to pill size (mirrors the glass-lab "subtle" ratios:
  // thin rim, gentle lensing, faint fringe, a touch of blur).
  const glassBezel = Math.max(4, Math.round(primaryHeight * 0.12));
  const glassStrength = Math.max(2, Math.round(primaryHeight * 0.07));
  const glassAberration = Math.max(0.5, primaryHeight * 0.02);
  const glassBlur = 0.5;
  const primaryRadius = primaryHeight / 2;
  const tabRadius = tabHeight / 2;
  const primaryBackdrop = backdropFor(primaryCenterDotX, primaryCenterDotY, primaryWidth, primaryHeight);
  const tabBackdrop = backdropFor(tabCenterDotX, tabCenterDotY, tabWidth, tabHeight);

  const tabTransition = isDragging ? { duration: 0 } : PLACEMENT_SPRING;

  const tabClassName = `${classNames.tab ?? DEFAULT_TAB_CN} absolute`;
  const primaryClassName = `${classNames.primary ?? DEFAULT_PRIMARY_CN} ${homeAccentClassName ?? ''} relative`;

  // Render the flag glyph for any city. Home cities get a circular ring
  // around the flag (iOS line 1391-1404: Circle().strokeBorder of accent
  // color, lineWidth 2.25px, 3px padding inside the ring).
  const renderFlag = (city: TimezoneData, isHomeFlag: boolean) => {
    const flagSpan = (
      <span
        style={{
          fontSize: flagFontPx,
          lineHeight: 1,
          display: 'inline-block',
        }}
      >
        {city.emoji}
      </span>
    );
    if (!isHomeFlag) {
      return flagSpan;
    }
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '9999px',
          // `outline` instead of `border` so the ring doesn't add layout
          // size (matches iOS `Circle().strokeBorder` overlay behavior — the
          // stroke is rendered around the wrapper's box but doesn't push
          // sibling elements). Pairs with `padding: 3` for the iOS-spec 3px
          // gap between flag glyph and ring.
          outline: `${HOME_FLAG_RING_WIDTH}px solid ${HOME_FLAG_RING_COLOR}`,
          outlineOffset: 0,
          padding: HOME_FLAG_RING_PADDING,
          lineHeight: 1,
          flex: '0 0 auto',
        }}
      >
        {flagSpan}
      </span>
    );
  };

  // Render helper — content layer for the primary pill (TIME + FLAG inline,
  // upright in screen space, placement-aware order). iOS line 1376-1387:
  // placement.left → [FLAG][TIME], placement.right → [TIME][FLAG]. Flag
  // always sits on the OUTWARD side (away from dial center), time on the
  // INWARD side.
  const renderPrimaryContent = (city: TimezoneData, isHomeCity: boolean) => {
    const formatted = formatCityTime(city, baseTime, timeFormat);
    const flag = renderFlag(city, isHomeCity);
    const time = (
      <span
        className="font-rounded font-bold tabular-nums tracking-tight whitespace-nowrap text-foreground"
        style={{ fontSize: timeFontPx, lineHeight: 1, flex: '0 0 auto' }}
      >
        {formatted}
      </span>
    );
    return (
      <div
        className={`${classNames.flag ?? DEFAULT_FLAG_CN} flex items-center justify-center w-full h-full`}
        style={{
          gap: contentGap,
          padding: `0 ${horizontalPad}px`,
        }}
      >
        {placement === 'left' ? (
          <>
            {flag}
            {time}
          </>
        ) : (
          <>
            {time}
            {flag}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {renderTrailArc && (
        <div
          className="absolute inset-0"
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            width: '100%',
            height: '100%',
            zIndex: 20,
          }}
        >
          <svg className="w-full h-full" viewBox="0 0 100 100" style={{ overflow: 'visible' }}>
            <path
              d={`
                M ${naturalX} ${naturalY}
                A ${radius} ${radius} 0 ${!shouldTakeShortPath ? 1 : 0} ${sweepFlag ? 1 : 0} ${dotX} ${dotY}
              `}
              fill="none"
              stroke="rgba(37, 99, 235, 0.4)"
              strokeWidth="1"
              strokeDasharray="1.5 1"
            />
          </svg>
        </div>
      )}

      {/* Pills are rendered UPRIGHT — no rotation, no counter-rotation.
          Primary pill is positioned at (dotX, dotY)% with placement-aware
          [FLAG][TIME] / [TIME][FLAG] order. Optional cluster tab is a
          single capsule of secondary flag glyphs, attached to the
          time-side of the primary and tucked under its rounded end-cap
          via the iOS overlap formula. */}

      {/* Secondary tab — ONE capsule with flag glyphs only (iOS line
          855-940). Rendered before primary so primary paints on top in
          the overlap region.

          The tab's hidden-inset section is masked OUT entirely (CSS
          `mask-image` linear gradient), not just z-stacked under the
          primary. This is necessary because both pills are translucent
          (`material-thin` = 60% white + backdrop-blur), so the tab's
          rounded left end-cap would otherwise show THROUGH the primary
          and produce a visible "stacked pills with gap" artifact that
          iOS doesn't suffer from (SwiftUI `.ultraThinMaterial` composites
          differently). */}
      {showsTab && (
        <motion.div
          className={tabClassName}
          style={{
            left: `${tabCenterDotX.toFixed(3)}%`,
            top: `${tabCenterDotY.toFixed(3)}%`,
            width: tabWidth,
            height: tabHeight,
            marginLeft: -tabWidth / 2,
            marginTop: -tabHeight / 2,
            zIndex: 20,
            boxShadow: GLASS_BEVEL_SHADOW,
            // Mask out the hidden-inset side so the tab visually starts
            // exactly at the seam. On placement.left the tab is on the
            // RIGHT of primary → hidden inset on LEFT of tab → mask the
            // left `tabOverlap` px. Mirrored for placement.right.
            WebkitMaskImage:
              placement === 'left'
                ? `linear-gradient(to right, transparent 0, transparent ${tabOverlap}px, black ${tabOverlap}px, black 100%)`
                : `linear-gradient(to right, black 0, black ${tabWidth - tabOverlap}px, transparent ${tabWidth - tabOverlap}px, transparent 100%)`,
            maskImage:
              placement === 'left'
                ? `linear-gradient(to right, transparent 0, transparent ${tabOverlap}px, black ${tabOverlap}px, black 100%)`
                : `linear-gradient(to right, black 0, black ${tabWidth - tabOverlap}px, transparent ${tabWidth - tabOverlap}px, transparent 100%)`,
          }}
          initial={false}
          animate={{ x: 0 }}
          transition={tabTransition}
        >
          {/* Refraction layer — the dial gradient slice behind this tab. */}
          <PillGlass
            width={tabWidth}
            height={tabHeight}
            radius={tabRadius}
            refractionBackground={tabBackdrop.image}
            backdropSize={tabBackdrop.size}
            backdropPosition={tabBackdrop.position}
            bezel={glassBezel}
            strength={glassStrength}
            aberration={glassAberration}
            blur={glassBlur}
          />
          {/* Time-of-day tint — uses the primary's tint since secondaries
              in the same 15-min bucket share its dial position. */}
          <div
            className="absolute inset-0 rounded-full"
            style={{ backgroundColor: GLASS_FROST }}
            aria-hidden="true"
          />
          <div
            className={`${classNames.flag ?? DEFAULT_FLAG_CN} flex items-center h-full`}
            style={{
              gap: contentGap,
              // Asymmetric padding: hidden-overlap side (`tabOverlap +
              // tabInnerGap`) tucks under primary; outer-cap side
              // (`tabOuterPad`) breathes against the rounded end.
              paddingLeft:
                placement === 'left' ? tabOverlap + tabInnerGap : tabOuterPad,
              paddingRight:
                placement === 'left' ? tabOuterPad : tabOverlap + tabInnerGap,
              justifyContent: placement === 'left' ? 'flex-start' : 'flex-end',
            }}
          >
            {tabFlags.map((secondary) => {
              const isHomeSecondary = secondary.locationKey === homeCity;
              return (
                <span
                  key={secondary.locationKey}
                  style={{ flex: '0 0 auto', display: 'inline-flex', alignItems: 'center' }}
                >
                  {renderFlag(secondary, isHomeSecondary)}
                </span>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Primary capsule — upright, centered on (dotX, dotY) via the
          margin-offset trick. Home pill scales 1.08x and gets a 12% blue
          inner tint; the home distinguisher RING lives on the flag glyph,
          not the whole pill (iOS line 1391-1404). */}
      <div
        className={primaryClassName}
        style={{
          position: 'absolute',
          left: `${primaryCenterDotX.toFixed(3)}%`,
          top: `${primaryCenterDotY.toFixed(3)}%`,
          width: primaryWidth,
          height: primaryHeight,
          marginLeft: -primaryWidth / 2,
          marginTop: -primaryHeight / 2,
          // No `scale(1.08)` on the home pill: iOS doesn't scale the
          // capsule (TimeYWimeyClockSurface.swift:994-1000, 1393-1399). A
          // post-layout scale also shifts primary's right edge relative
          // to the tab and reintroduces a visible seam in home-city
          // clusters. Home distinction lives entirely on the flag (blue
          // accent ring) and the 12% blue inner tint.
          transformOrigin: 'center',
          zIndex: 30,
          boxShadow: GLASS_BEVEL_SHADOW,
        }}
      >
        {/* Refraction layer — the dial gradient slice behind this pill. */}
        <PillGlass
          width={primaryWidth}
          height={primaryHeight}
          radius={primaryRadius}
          refractionBackground={primaryBackdrop.image}
          backdropSize={primaryBackdrop.size}
          backdropPosition={primaryBackdrop.position}
          bezel={glassBezel}
          strength={glassStrength}
          aberration={glassAberration}
          blur={glassBlur}
        />
        <div
          className="absolute inset-0 rounded-full"
          style={{ backgroundColor: GLASS_FROST }}
          aria-hidden="true"
        />
        {/* Home is marked by the blue ring around the FLAG glyph only (see
            renderFlag) — matching iOS. No whole-pill blue fill. */}
        {renderPrimaryContent(primary, primaryIsHome)}
      </div>
    </>
  );
}

/** Exposed for `CityClocks` so bucket grouping shares the exact same angle
 *  formula the marker uses to position itself. */
export function clusterAngleFor(city: TimezoneData, baseTime: Date): number {
  return cityAngle(city, baseTime);
}

/** 96 buckets of 3.75° each — one per 15-min slice of a 24-hour dial. */
export function clusterBucketKey(angle: number): number {
  return ((Math.round(angle / 3.75) % 96) + 96) % 96;
}
